import React, { useState } from 'react';
import { X, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Note } from '@/types';
import ConfirmDeleteDialog from '../common/ConfirmDeleteDialog';

interface RecentNotesPanelProps {
  notes: Note[];
  onSelectNote: (note: Note) => void;
  onClose?: () => void;
  onDeleteNote: (note_id: string) => void;
  selectedNoteId?: string;
}

// Utility to extract a preview string from Tiptap JSON content
function getNotePreview(content: any): string {
  let json;
  if (!content) return '';
  if (typeof content === 'string') {
    try {
      json = JSON.parse(content);
    } catch {
      return content.slice(0, 100); // fallback: show first 100 chars
    }
  } else {
    json = content;
  }
  if (!json.content) return '';
  // Try to find first heading or paragraph
  const node = json.content.find(
    (n: any) =>
      (n.type === 'heading' || n.type === 'paragraph') &&
      n.content &&
      n.content.length > 0
  );
  if (node) {
    return node.content.map((c: any) => c.text).join(' ').slice(0, 100);
  }
  return '';
}

const RecentNotesPanel = ({ notes, onSelectNote, onClose, onDeleteNote, selectedNoteId }: RecentNotesPanelProps) => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const noteToDelete = notes.find(n => n.note_id === deleteId);

  return (
    <>
      {/* Mobile Bottom Sheet */}
      <div className="block md:hidden">
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={onClose}
        />
        <div 
          className="fixed bottom-0 left-0 right-0 z-50 bg-background rounded-t-2xl shadow-lg h-[60vh] transition-transform duration-300 ease-in-out p-4"
        >
          {/* Drag handle */}
          <div className="w-full flex flex-col items-center cursor-grab active:cursor-grabbing pt-2">
            <div className="w-12 h-1 bg-muted rounded-full mb-4" />
          </div>
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-border">
            <h2 className="font-semibold text-lg">Recent Notes</h2>
            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          {/* Content */}
          <div className="overflow-y-auto h-[calc(60vh-89px)] pt-2">
            {notes.map((note) => (
              <div key={note.note_id || note.title || Math.random()} className="relative group">
                <button
                  onClick={() => onSelectNote(note)}
                  className={cn(
                    "group relative select-none flex flex-col gap-3 px-4 py-2 rounded-lg bg-muted/50 hover:bg-accent/40 border border-border/10 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/5 active:translate-y-0 active:shadow-md h-full cursor-pointer w-full text-left mb-2",
                    note.note_id === selectedNoteId && "border-2 border-hintword-orange/50 bg-hintword-orange/10"
                  )}
                >
                  <h3 className="font-medium truncate">{note.title}</h3>
                  <div className="text-sm text-muted-foreground recent-note-content line-clamp-2">
                    {getNotePreview(note.content)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {note.updated_at && !isNaN(Date.parse(note.updated_at))
                      ? new Date(note.updated_at).toLocaleDateString()
                      : "Draft"}
                  </p>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => { e.stopPropagation(); setDeleteId(note.note_id); }}
                  title="Delete note"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
          <ConfirmDeleteDialog
            open={!!deleteId}
            title="Delete Note?"
            description={`Are you sure you want to delete "${noteToDelete?.title || 'this note'}"? This action cannot be undone.`}
            onCancel={() => setDeleteId(null)}
            onConfirm={() => { if (deleteId) { onDeleteNote(deleteId); setDeleteId(null); } }}
            confirmLabel="Delete"
            cancelLabel="Cancel"
          />
        </div>
      </div>
      {/* Desktop Side Panel */}
      <div className="hidden md:block">
        <div className="fixed top-0 right-0 w-full md:w-[400px] h-screen bg-background border-l border-border shadow-lg z-50 p-4">
          <div className="flex items-center justify-between pb-4 border-b border-border">
            <h2 className="font-semibold text-lg">Recent Notes</h2>
            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="overflow-y-auto h-[calc(100vh-57px)] pt-2">
            {notes.map((note) => (
              <div key={note.note_id || note.title || Math.random()} className="relative group">
                <button
                  onClick={() => onSelectNote(note)}
                  className={cn(
                    "group relative select-none flex flex-col gap-3 px-4 py-2 rounded-lg bg-muted/50 hover:bg-accent/40 border border-border/10 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/5 active:translate-y-0 active:shadow-md h-full cursor-pointer w-full text-left mb-2",
                    note.note_id === selectedNoteId && "border-2 border-hintword-orange/50 bg-hintword-orange/10"
                  )}
                >
                  <h3 className="font-medium truncate">{note.title}</h3>
                  <div className="text-sm text-muted-foreground recent-note-content line-clamp-2">
                    {getNotePreview(note.content)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {note.updated_at && !isNaN(Date.parse(note.updated_at))
                      ? new Date(note.updated_at).toLocaleDateString()
                      : "Draft"}
                  </p>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => { e.stopPropagation(); setDeleteId(note.note_id); }}
                  title="Delete note"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
          <ConfirmDeleteDialog
            open={!!deleteId}
            title="Delete Note?"
            description={`Are you sure you want to delete "${noteToDelete?.title || 'this note'}"? This action cannot be undone.`}
            onCancel={() => setDeleteId(null)}
            onConfirm={() => { if (deleteId) { onDeleteNote(deleteId); setDeleteId(null); } }}
            confirmLabel="Delete"
            cancelLabel="Cancel"
          />
        </div>
      </div>
    </>
  );
};

export default RecentNotesPanel; 